import { GoogleOutlined } from '@ant-design/icons';
import { Button, Form, Input, message, Modal, Tabs } from 'antd';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import HttpClient from 'src/services/http';
import { createUserReq } from 'src/services/userService';
import { login } from 'src/store/user/actions';
import { API_VERSION } from 'src/utils/constants';

type Props = {
  visible: boolean;
  onClose: () => void;
};

const LoginModal: FC<Props> = ({ visible, onClose }) => {
  const { t }: any = useTranslation();
  const [form] = Form.useForm();
  const [loginForm] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [registerLoading, setRegisterLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('login');

  const _login = useConnectedAction(login);

  useEffect(() => {
    // Check for token in URL when component mounts
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (token) {
      // Remove token from URL without refreshing the page
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);

      // Close the modal
      onClose();

      // Show success message
      message.success(t('loginSuccess'));
    }
  }, [onClose, t]);

  const handleRegister = async (values: any): Promise<void> => {
    try {
      setRegisterLoading(true);
      const data: any = {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        password: values.password,
      };
      if (values.promo) {
        data.promoCode = values.promo;
      }

      const res = await createUserReq(data);

      if (HttpClient.isOkStatus(res.status)) {
        message.success(t('registrationSuccessful'));
        _login({ username: values.email, password: values.password });
        onClose();
      } else {
        // Handle API error responses
        let errorMessage = t('registrationFailed');

        if (res.response?.data?.message) {
          errorMessage = res.response.data.message;
        } else if (res.message) {
          errorMessage = res.message;
        }

        // Show specific error messages based on the error content
        if (errorMessage.toLowerCase().includes('duplicate')) {
          errorMessage = t('userAlreadyExists');
        }

        message.error(errorMessage);
      }
    } catch (error) {
      console.error('Registration error:', error);
      message.error(t('registrationFailed'));
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleLogin = async (values: any): Promise<void> => {
    _login({ username: values.email, password: values.password });
    onClose();
  };

  const handleGoogleLogin = async (): Promise<void> => {
    try {
      setLoading(true);
      let path = `/${API_VERSION}/auth/google`;
      const promoCode = form.getFieldValue('promoCode');
      if (promoCode) {
        path += `?promoCode=${promoCode}`;
      }

      // Redirect to Google auth
      window.location.href = `${process.env.REACT_APP_REMOTE}${path}`;

      onClose();
    } catch (error) {
      console.error('Login failed:', error);
      message.error(error instanceof Error ? error.message : t('loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const items = [
    {
      key: 'login',
      label: t('login'),
      children: (
        <Form form={loginForm} layout="vertical" onFinish={handleLogin} className="login-form">
          <Form.Item name="email" label={t('email')} rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="password" label={t('password')} rules={[{ required: true }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              {t('login')}
            </Button>
          </Form.Item>
          <Form.Item>
            <Button icon={<GoogleOutlined />} block onClick={handleGoogleLogin} loading={loading}>
              {t('loginWithGoogle')}
            </Button>
          </Form.Item>
        </Form>
      ),
    },
    {
      key: 'register',
      label: t('register'),
      children: (
        <Form form={form} layout="vertical" onFinish={handleRegister} className="register-form">
          <Form.Item name="firstName" label={t('firstName')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="lastName" label={t('lastName')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label={t('email')} rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="password" label={t('password')} rules={[{ required: true }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="confirm"
            label={t('confirmPassword')}
            dependencies={['password']}
            rules={[
              { required: true },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) return Promise.resolve();
                  return Promise.reject(new Error(t('passwordsDoNotMatch')));
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item name="promo" label={t('promoCode')}>
            <Input placeholder={t('optional')} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={registerLoading}>
              {t('register')}
            </Button>
          </Form.Item>
          <Form.Item>
            <Button icon={<GoogleOutlined />} block onClick={handleGoogleLogin} loading={loading}>
              {t('loginWithGoogle')}
            </Button>
          </Form.Item>
        </Form>
      ),
    },
  ];

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title={t('loginOrRegister')}
      footer={null}
      styles={{ body: { maxHeight: '60vh', overflowY: 'auto', paddingRight: '12px' } }}
      className="login-modal"
      destroyOnClose
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={items} className="auth-tabs" />
    </Modal>
  );
};

export default LoginModal;
